import { useState, type FormEvent } from 'react';
import type { HandsetModel } from '@arcnet/contracts';
import { useHandsetCatalogue, useSaveHandsetModel } from '../../lib/api/hooks-provisioning.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';

/**
 * The supported-handset catalogue (docs/26 C1).
 *
 * The phones the platform supports, shared by every account, and what
 * provisioning does for each: how many lines one holds, whether its keys can
 * open a login screen, and whether the platform writes its configuration or
 * lists the settings to type in. Adding a handset to an account picks from
 * here, so a model's figures are set once rather than typed at every customer.
 */

const MAKES = [
  ['yealink', 'Yealink'], ['fanvil', 'Fanvil'], ['grandstream', 'Grandstream'],
  ['polycom', 'Poly'], ['snom', 'Snom'], ['cisco', 'Cisco'],
] as const;

export function HandsetCatalogPage() {
  const can = useSession((s) => s.can);
  const writable = can('handsetmodel:write');
  const catalogue = useHandsetCatalogue();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<HandsetModel | null>(null);

  const rows = catalogue.data?.data ?? [];
  const inUse = rows.reduce((n, m) => n + m.device_count, 0);

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Handsets</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            {rows.length} model{rows.length === 1 ? '' : 's'} · {inUse} handset{inUse === 1 ? '' : 's'} in use
          </p>
        </div>
        {writable && (
          <button type="button" className="arc-btn arc-btn-primary"
            onClick={() => { setAdding((v) => !v); setEditing(null); }}>
            <Icon name={adding ? 'close' : 'plus'} size={13} />
            {adding ? 'Cancel' : 'Add model'}
          </button>
        )}
      </div>

      {catalogue.error && <ErrorBanner error={catalogue.error} />}

      {writable && (adding || editing) && (
        <ModelEditor key={editing?.id ?? 'new'} existing={editing}
          onDone={() => { setAdding(false); setEditing(null); }} />
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Handset</th>
                <th className="arc-td-num">Lines</th>
                <th className="arc-td-num">Keys</th>
                <th>Configuration</th>
                <th>Login screen</th>
                <th className="arc-td-num">In use</th>
                <th />
              </tr>
            </thead>
            {catalogue.data === undefined ? <TableSkeleton cols={7} /> : (
              <tbody>
                {rows.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <span style={{ fontWeight: 550 }}>{m.vendor_label} {m.model}</span>
                      {!m.active && <> <Badge tone="muted">no longer offered</Badge></>}
                      {m.notes && <div className="arc-subtle" style={{ fontSize: 11.5 }}>{m.notes}</div>}
                    </td>
                    <td className="arc-td-num">{m.max_lines}</td>
                    <td className="arc-td-num">
                      {m.line_keys ?? <span className="arc-subtle">—</span>}
                    </td>
                    <td>
                      <Badge tone={m.generates_config ? 'ok' : 'muted'}>
                        {m.generates_config ? 'generated' : 'by hand'}
                      </Badge>
                    </td>
                    <td className="arc-muted">
                      {m.xml_browser && m.desk_pages ? 'yes'
                        : m.xml_browser ? <span className="arc-subtle">phone can; screens not written</span>
                          : <span className="arc-subtle">no</span>}
                    </td>
                    <td className="arc-td-num arc-muted">
                      {m.device_count || <span className="arc-subtle">—</span>}
                    </td>
                    <td className="arc-td-right">
                      {writable && (
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { setEditing(m); setAdding(false); }}>Edit</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {catalogue.data !== undefined && rows.length === 0 && (
          <Empty icon="phone" title="No handsets in the catalogue" hint="Add the models you support." />
        )}
      </div>

      {/* Said once, because an empty Keys cell changes what provisioning does. */}
      <p className="arc-subtle" style={{ fontSize: 11.5, marginTop: 12 }}>
        Keys are the programmable keys on the phone itself. Where it is not recorded, a template’s
        keys are written and none are cleared.
      </p>
    </>
  );
}

function ModelEditor({ existing, onDone }: { existing: HandsetModel | null; onDone: () => void }) {
  const save = useSaveHandsetModel();
  const [vendor, setVendor] = useState<string>(existing?.vendor ?? 'yealink');
  const [model, setModel] = useState(existing?.model ?? '');
  const [maxLines, setMaxLines] = useState(existing ? String(existing.max_lines) : '');
  const [keys, setKeys] = useState(existing?.line_keys != null ? String(existing.line_keys) : '');
  const [xml, setXml] = useState(existing?.xml_browser ?? false);
  const [active, setActive] = useState(existing?.active ?? true);
  const [notes, setNotes] = useState(existing?.notes ?? '');

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        const figures = {
          max_lines: Number(maxLines),
          line_keys: keys.trim() ? Number(keys) : null,
          xml_browser: xml,
          active,
          notes: notes.trim() || null,
        };
        save.mutate(
          existing ? { id: existing.id, ...figures } : { vendor, model: model.trim(), ...figures },
          { onSuccess: onDone },
        );
      }}>
      <h3 className="arc-h2" style={{ marginBottom: 12 }}>
        {existing ? `${existing.vendor_label} ${existing.model}` : 'Add a model'}
      </h3>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 12, marginBottom: 12,
      }}>
        {!existing && (
          <>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="hm-vendor">Make</label>
              <select id="hm-vendor" className="arc-input" value={vendor}
                onChange={(e) => setVendor(e.target.value)}>
                {MAKES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="hm-model">Model</label>
              <input id="hm-model" className="arc-input" required maxLength={40} value={model}
                onChange={(e) => setModel(e.target.value)} placeholder="T54W" />
            </div>
          </>
        )}
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="hm-lines">Lines</label>
          <input id="hm-lines" className="arc-input" type="number" min={1} max={24} required
            value={maxLines} onChange={(e) => setMaxLines(e.target.value)} />
          <p className="arc-hint">SIP accounts it holds.</p>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="hm-keys">Keys</label>
          <input id="hm-keys" className="arc-input" type="number" min={0} max={120}
            value={keys} onChange={(e) => setKeys(e.target.value)} placeholder="not recorded" />
          <p className="arc-hint">Programmable keys on the phone.</p>
        </div>
      </div>

      <div className="arc-row" style={{ gap: 10, marginBottom: 8 }}>
        <Switch id="hm-xml" checked={xml} onChange={setXml} />
        <label htmlFor="hm-xml" style={{ cursor: 'pointer' }}>
          A key can open a page on its screen (XML browser)
        </label>
      </div>
      {existing && (
        <div className="arc-row" style={{ gap: 10, marginBottom: 8 }}>
          <Switch id="hm-active" checked={active} onChange={setActive} />
          <label htmlFor="hm-active" style={{ cursor: 'pointer' }}>Offered when adding a handset</label>
        </div>
      )}
      <div className="arc-field">
        <label className="arc-label" htmlFor="hm-notes">Notes</label>
        <input id="hm-notes" className="arc-input" maxLength={500} value={notes}
          onChange={(e) => setNotes(e.target.value)} />
      </div>

      <div className="arc-row" style={{ gap: 8 }}>
        <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : existing ? 'Save' : 'Add model'}
        </button>
        <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}
